import React from "react";
import { useNavigate } from "react-router-dom";

/**
 * Floating AI search button — sibling to <WhatsAppFloat />.
 * Opens a sleek pop-up where the user can describe their requirement in
 * natural language; submission navigates to /properties?ai_query=...
 * which the Properties page intercepts and shows AI-matched results.
 *
 * Visible site-wide, sits bottom-right just above the WhatsApp pill.
 */
export default function AiSearchFloat() {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState("");
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const submit = (e) => {
    e?.preventDefault();
    const v = q.trim();
    if (!v) return;
    navigate(`/properties?ai_query=${encodeURIComponent(v)}`);
    setOpen(false);
    setQ("");
  };

  const suggestions = [
    "10,000 sqft warehouse in JAFZA with loading bays",
    "Cold storage in DIP for pharma, GDP certified",
    "Staff accommodation 100+ rooms near Al Quoz",
    "Industrial plot for re-export in NIP",
  ];

  return (
    <>
      {/* Trigger button — bottom-right, above WhatsApp */}
      <button
        type="button"
        data-testid="ai-float-btn"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open AI smart search"
        className={`fixed bottom-24 right-5 md:right-7 z-40 group flex items-center gap-2 pl-3 pr-4 py-3 rounded-full shadow-2xl transition-all
          ${open ? "bg-[#FFCC00] text-[#031833]" : "bg-[#031833] text-[#FFCC00] hover:bg-[#0A2455]"}`}
      >
        <span className={`w-7 h-7 rounded-full flex items-center justify-center font-display text-base
          ${open ? "bg-[#031833] text-[#FFCC00]" : "bg-[#FFCC00] text-[#031833]"}`}
        >
          AI
        </span>
        <span className="font-mono-tech text-xs uppercase tracking-wider hidden sm:inline">
          {open ? "Close" : "Ask AI"}
        </span>
        <span className={`absolute -top-1 -right-1 w-3 h-3 rounded-full bg-[#FFCC00] ${open ? "hidden" : ""} animate-pulse`}></span>
      </button>

      {/* Popover panel */}
      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]"
            onClick={() => setOpen(false)}
          />
          <div
            data-testid="ai-float-panel"
            className="fixed bottom-44 right-4 md:right-7 z-50 w-[calc(100vw-2rem)] sm:w-[420px] bg-zinc-950 text-white border-2 border-[#FFCC00] shadow-2xl"
          >
            <div className="p-4 border-b border-zinc-800">
              <div className="font-mono-tech text-[10px] uppercase tracking-wider text-[#FFCC00] mb-1">
                // AI SMART SEARCH
              </div>
              <div className="font-display text-xl tracking-tighter">
                Describe what you need.
              </div>
              <div className="font-mono-tech text-[10px] uppercase text-zinc-500 mt-1">
                English or العربية · We match in seconds.
              </div>
            </div>
            <form onSubmit={submit} className="p-4">
              <textarea
                ref={inputRef}
                data-testid="ai-float-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="e.g. 15,000 sqft bonded warehouse in JAFZA with 9m clear height and 4 loading bays"
                rows={3}
                className="w-full bg-zinc-900 border border-zinc-800 text-white px-3 py-2 font-mono-tech text-sm focus:border-[#FFCC00] focus:outline-none placeholder:text-zinc-600"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    submit();
                  }
                }}
              />
              <div className="mt-3 flex flex-wrap gap-1.5">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setQ(s)}
                    className="font-mono-tech text-[10px] uppercase text-zinc-400 hover:text-[#FFCC00] border border-zinc-800 hover:border-[#FFCC00] px-2 py-1 tracking-wider transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
              <button
                type="submit"
                data-testid="ai-float-submit"
                disabled={!q.trim()}
                className="mt-4 w-full bg-[#FFCC00] text-[#031833] font-display tracking-tighter text-lg py-3 hover:bg-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                MATCH PROPERTIES →
              </button>
            </form>
          </div>
        </>
      )}
    </>
  );
}
